// @flow

import * as React from 'react';
import PropTypes from 'prop-types';
import R from 'ramda';
import classNames from 'classnames';

import AuthTitle from './AuthTitle';
import StatusIcon from './StatusIcon';
import type { TitleProps } from '../../common/types/titleTypes';

// https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input
const VALID_AUTH_INPUT_PROPS = [
  'accept',
  'autoComplete',
  'autoFocus',
  'capture',
  'checked',
  'disabled',
  'form',
  'formAction',
  'formEnctype',
  'formMethod',
  'formNoValidate',
  'formTarget',
  'height',
  'incremental',
  'inputMode',
  'list',
  'max',
  'min',
  'multiple',
  'name',
  'onFocus',
  'onBlur',
  'pattern',
  'placeholder',
  'readOnly',
  'required',
  'selectionDirection',
  'size',
  'spellCheck',
  'src',
  'step',
  'tabIndex',
  'type',
  'webkitDirectory',
  'width'
];

// Please use instead the relevant core components for the following types:
// TODO: Add 'file' to this list, in order to only
// allow only ImageUploader component to apply type="file"
const INVALID_TYPES = ['button', 'checkbox', 'radio'];

type Props = {
  /**
   * required id for automation testing
   */
  testid: string,
  /**
   * The value of the input field
   */
  value?: ?string,
  /**
   * Component to replace the input tag
   */
  customChildComponent?: React.Element<any>,
  /**
   * Append classes to input container
   */
  className?: string,
  /**
   * Additonal note to display below the input field ('description' goes above)
   * Please see renderFootnote for further clarification.
   */
  footnote?: string | React.Element<any>,
  /**
   * Label for mixpanel tracking
   * NOTE: If no text 'label' prop is passed in, you need to pass
   *       either an i13nLabel or i13nModel.
   */
  i13nLabel?: string,
  /**
   * Model passed into the mixpanel event. (Not necessary for most use cases).
   * label attribute can be overridden by i13nLabel.
   *
   * Order of label precendence:
   * 1. i13nLabel
   * 2. i13nModel.label
   * 3. [MOST COMMON] If the label is text, we will default to it.
   */
  i13nModel?: { label: string, params: {} },
  /**
   * Append classes to input element
   */
  inputClassName?: string,
  /**
   * Maxium input length
   */
  maxLength?: number,
  /**
   * Minimum input length
   */
  minLength?: number,
  /**
   * Ensures no extra space left below the input field
   */
  noFooter?: boolean,
  /**
   * callback to parent on input text change
   * required in all scenarios EXCEPT when the input field is always disabled
   * and therefore cannot change
   */
  onChange?: (event: {
    target: { value: string },
    value: string,
    error: ?boolean
  }) => void,
  /**
   * Placeholder text
   */
  placeholder?: string,
  /**
   * Determines color of input field, as well as whether to display
   * the corresponding statusFootnote or not.
   */
  statusType?: 'error' | 'warning' | 'success',
  /**
   * If passed in AND statusType is passed in, the corresponding message will
   * display as a footer. Please see renderFootnote for further clarification.
   */
  statusFootnotes?: ?{
    error?: ?string,
    warning?: ?string,
    success?: ?string
  },
  /**
   * Input type, see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input
   * NOTE: Cannot be one of the INVALID_TYPES (button, checkbox, or radio)
   */
  type?: string,
  /**
   * Allows you to pass in an optional message to show in case of
   * validation error rather than showing the browser default.
   * Please see renderFootnote for further clarification.
   */
  validationMessage?: ?string,
  /**
   * This prop is passed down from the ScreenSignup component
   * and triggers the render of the green check icon if the input is valid.
   */
  inputValid?: boolean,
  /**
   * This prop is passed down from the ScreenSignup component
   * and helps to determine when to render the success or fail icon in the input field.
   * No icon will be rendered until we know some kind of validation has occured.
   */
  inputChecked?: boolean,
  /**
   * This prop is passed down from the ScrenSignup component and applies red border styling on the
   * input field if there is an error with the input.
   */
  hasError?: boolean,
  /**
   * This is to disable copying from the password input field.
   */
  pasteDisabled?: boolean,
  /**
   * This is to display the Show/Hide icon next to the title
   */
  displayShowHide?: boolean,
  /**
   * This function toggles the state of showPassword to show or hide the password input.
   */
  onShowHide?: () => void
} & TitleProps;

type State = {
  value: ?string,
  error: ?boolean,
  showPassword: ?boolean
};

class AuthInput extends React.PureComponent<Props, State> {
  textInput: ?React.Element<*>;
  setTextInputRef = (element: ?React.Element<*>): void => {
    this.textInput = element;
  };

  static defaultProps = {
    type: 'text',
    onChange: () => {}
  };

  static contextTypes = {
    i13n: PropTypes.object
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      value: this.props.value || '',
      error: null,
      showPassword: false
    };
    const labelIsText = props.label && typeof props.label === 'string';
    if (!props.i13nLabel && !props.i13nModel && !labelIsText) {
      console.warn(
        'Either a text label, i13nLabel, or i13nModel must be passed to the Input component.'
      );
    }
    if (R.contains(props.type, INVALID_TYPES)) {
      console.warn(
        'Input component is not meant for buttons, checkboxes, or radios. Please use the corresponding core component instead.'
      );
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    const { value } = nextProps;

    this.setState({
      value: value === undefined || value === null ? '' : value
    });
  }

  fireMixpanelEvent = (value: string) => {
    if (!this.context.i13n) {
      return;
    }
    // Set defaults
    let i13nModel = this.props.i13nModel || {};
    let label = this.props.label || '';

    if (!label || typeof label !== 'string') {
      label = this.props.i13nLabel || i13nModel.label || '';
    }

    // Merge params
    const params = R.merge({ component: 'Input', value }, i13nModel.params);
    // Final merge in case there are other properties passed besides label and params
    i13nModel = R.merge(i13nModel, { label, params });

    if (this.props.type === 'password') {
      i13nModel.params.value = 'Password changed';
    }
    this.context.i13n.executeEvent('log', i13nModel);
  };

  onChange = (e: { target: { value: string } }) => {
    const target = e.target;
    // TODO: fire Mixpanel events, with a debounce
    // this.fireMixpanelEvent(target.value);
    this.setState({ value: target.value }, () => {
      this.props.onChange &&
        this.props.onChange({
          target,
          value: this.state.value || '',
          error: this.state.error
        });
    });
  };

  movedOutOfFocus = (e: { target: { value: string } }) => {
    const value = R.path(['target', 'value'], e);
    const target = R.path(['target'], e);

    if (value && typeof value === 'string') {
      e = {
        ...e,
        target: {
          ...target,
          value: value.trim()
        }
      };
    }
    this.onChange(e);
  };

  renderFootnote = () => {
    const props = this.props;
    if (props.noFooter) {
      return null;
    }

    /* FOOTNOTE: A footnote will appear below the input if:
     *   1. Both 'statusType' and 'statusFootnotes' are passed in from the parent,
     *      AND there exists a property on 'statusFootnotes'
     *      matching the 'statusType' passed in.
     *
     *      Example props:
     *         props.statusType = 'warning'
     *         props.statusFootnotes = {
     *             warning: 'Your input may cause issues.'
     *         }
     *         props.footnote = 'This is important to use Branch properly.'
     *
     *       Will display: 'Your input may cause issues.'
     *
     *    2. A 'footnote' prop is passed in, and the above condition is not met.
     *
     *       Example props:
     *          props.statusType = null
     *          props.statusFootnotes = {
     *              warning: 'Your input may cause issues.'
     *          }
     *          props.footnote = 'This is important to use Branch properly.'
     *
     *       Will display: 'This is important to use Branch properly.'
     */
    const statusFootnote =
      !!props.statusType &&
      !!props.statusFootnotes &&
      props.statusFootnotes[props.statusType];
    const footnote = statusFootnote || props.footnote;

    return (
      <span
        className="input-control__footer h_d-f"
        data-testid={`${props.testid}__footer`}
      >
        {footnote && (
          <span className="input-control__note h_ta-start">{footnote}</span>
        )}
      </span>
    );
  };

  // This is to disable copy from the password input field.
  _handlePaste = (e: SyntheticEvent<>) => {
    if (!!this.props.pasteDisabled) {
      return;
    }
    e.preventDefault();
  };

  render() {
    const {
      className,
      inputClassName,
      testid,
      customChildComponent,
      statusType,
      label,
      inputValid,
      placeholder,
      inputChecked,
      displayShowHide,
      onShowHide
    } = this.props;

    return (
      <div
        className={classNames('input-control', className, {
          [`input-control--${statusType || ''}`]: !!statusType
        })}
        data-testid={testid}
      >
        {typeof label === 'string' ? (
          <AuthTitle
            label={label}
            description={this.props.description}
            labelClassName={this.props.labelClassName}
            hoverableTooltip={this.props.hoverableTooltip}
            tooltip={this.props.tooltip}
            placeholder={placeholder}
            displayShowHide={displayShowHide}
            onShowHide={onShowHide}
          />
        ) : (
          label
        )}
        {customChildComponent ? (
          customChildComponent
        ) : (
          <div className="h_d-f">
            <input
              className={classNames(
                'input-control__input h_bdrs-2px h_w-100',
                inputClassName,
                { ['has-error']: !!this.props.hasError }
              )}
              {...R.pick(VALID_AUTH_INPUT_PROPS, this.props)}
              value={this.state.value}
              onChange={this.onChange}
              data-testid={`${testid}-input`}
              ref={this.setTextInputRef}
              onBlur={this.movedOutOfFocus}
              onCopy={this._handlePaste}
            />
            {inputChecked ? (
              <StatusIcon success={inputValid} testid={label} />
            ) : null}
          </div>
        )}
        {this.renderFootnote()}
      </div>
    );
  }
}

export default AuthInput;
